import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IPagination } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  ACTIVE_INACTIVE_HSN_SAC_CODE,
  DELETE_HSN_SAC_CODE,
  GET_ALL_HSN_SAC_CODE,
} from "../../services/Endpoints";

import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
  toastSuccess,
} from "../../utils/AppFunctions";

export interface IHsnSacCode {
  id: string;
  hsn_sac_code: string;
  chapter: string;
  description: string;
  rate_percentage: [];
  effective_from: string;
  end_date: Date | undefined;
  is_active: TBitValue;
  is_deleted?: TBitValue;
}

export interface IHsnSacCodeAdvancedSearch {
  sd: TBitValue;
}

export const HSN_SAC_CODE_ADVANCE_SEARCH_INITIAL_VALUE = {
  sd: BIT_VALUE.Zero,
};

export function getHsnSacCodeFields<T>(
  actionComponent: (item: T) => JSX.Element,
  handleFirmStatus: (item: T) => void,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("hsn_sac_code"),
      key: "hsn_sac_code",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("chapter"),
      key: "chapter",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("rate_percentage"),
      key: "rate_percentage",
      sort: false,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-3",
      format: ({ field, data }: ITabelFieldViewProps<T>) =>
        data[field.key].join(" | "),
    },
    {
      name: t("status"),
      key: "is_active",
      sort: false,
      type: TABLE_FIELD_TYPE.switch,
      align: TABLE_FIELD_ALIGN.center,
      checkedValue: "1",
      flex: "col-span-1",
      handleCheck: handleFirmStatus,
    },
    {
      name: t("effective_from"),
      key: "effective_from",
      sort: true,
      type: TABLE_FIELD_TYPE.date,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("end_date"),
      key: "end_date",
      sort: true,
      type: TABLE_FIELD_TYPE.date,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-1",
      component: actionComponent,
    },
  ];
}

export const getAllHsnSacCode = async (
  setHsnSacCodeList: TReactSetState<IHsnSacCode[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  advanceSearch: IHsnSacCodeAdvancedSearch
) => {
  try {
    const hsnSacCode = await GET_ALL_HSN_SAC_CODE(
      pagination,
      search,
      advanceSearch
    );
    if (hsnSacCode && hsnSacCode.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setHsnSacCodeList(hsnSacCode.data.item);
      setPagination(hsnSacCode.data.pagination);
    } else {
      setHsnSacCodeList([]);
      sweetAlertError({
        message: hsnSacCode.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "getAllHsnSacCode");
  }
};

export const activeInactiveHsnSacCode = async (
  id: string,
  values: TBitValue
) => {
  try {
    const activeInactiveData = await ACTIVE_INACTIVE_HSN_SAC_CODE(id, {
      is_active: values,
    });
    if (
      activeInactiveData &&
      activeInactiveData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      toastSuccess(activeInactiveData.message);
      return true;
    } else {
      sweetAlertError({
        message: activeInactiveData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "activeInactiveHsnSacCode");
    return false;
  }
};

export const deleteHsnSacCode = async (id: string) => {
  try {
    const deleteData = await DELETE_HSN_SAC_CODE(id);
    if (deleteData && deleteData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: deleteData.message });
      return true;
    } else {
      sweetAlertError({
        message: deleteData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "deleteHsnSacCode");
    return false;
  }
};
